import { InboxOutlined } from '@ant-design/icons'
import { Typography, Spin, Upload } from 'antd'
import { errorHandler } from 'hooks/errorHandler'
import { simpleSuccessNotification } from 'hooks/notificationHandler'
import { pathify } from 'shared'
import { rest } from 'utils/axiosInstance'
import { useGlobalState } from 'context/GlobalStateContext'
import { getWorkarounds } from '../../utils/getWorkaround'
import { useState } from 'react'
import { useAnyTrue } from '../../../../hooks/useAnyTrue'
import { useSharePointUploaderContext } from '../../SharePointUploaderContext'
import { SharePointDraggerProps } from './types'
import CIMSLink from '../../../CIMSLink'
import { RcFile } from 'antd/lib/upload'

const SharePointDragger: React.FC<SharePointDraggerProps> = ({
  generic,
  onChange,
  permittedFileExtensions = [
    'jpeg',
    'png',
    'jpg',
    'pdf',
    'csv',
    'doc',
    'docx',
    'xls',
    'xlsx',
    'xer',
  ],
  permittedFileTypes = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'application/pdf',
    'text/csv',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ],
}) => {
  const { refetch, loading, sharePointResource, folder, parent } =
    useSharePointUploaderContext()
  const {
    state: { projectGlobalId },
  } = useGlobalState()
  const [uploading, setUploading] = useState(false)
  const action = pathify('api', 'projects', projectGlobalId, 'files')
  const [allLoading] = useAnyTrue([loading, uploading])
  const folderName = generic ? 'SharePoint' : folder
  return (
    <div style={{ textAlign: 'center' }}>
      <Spin spinning={allLoading}>
        <Typography.Paragraph>
          <Typography.Text>
            Uploading to{' '}
            {sharePointResource?.webUrl ? (
              <CIMSLink external href={sharePointResource?.webUrl}>
                {folderName}
              </CIMSLink>
            ) : (
              folderName
            )}
          </Typography.Text>
        </Typography.Paragraph>
        <Upload.Dragger
          name="file"
          accept={[
            ...permittedFileExtensions.map((val) => `.${val}`),
            ...permittedFileExtensions,
          ].join(',')}
          multiple
          style={{ minWidth: 'fit-content', alignSelf: 'center' }}
          action={action}
          disabled={loading}
          showUploadList={false}
          customRequest={async ({
            action,
            file: f,
            onProgress,
            onSuccess,
            onError,
          }) => {
            try {
              const file = f as RcFile
              const formData = new FormData()
              formData.append('file', file)
              formData.append('folder', folder)
              formData.append('parentId', parent.id)
              formData.append('type', 'sharePoint')

              const splitArr = file.name.split('.')
              const extension = splitArr[splitArr.length - 1].toLowerCase()

              if (
                !!permittedFileTypes.length &&
                !permittedFileTypes.includes(file.type.toLowerCase()) &&
                extension !== 'xer'
              )
                throw new Error('Invalid file type.')
              if (
                !!permittedFileExtensions.length &&
                !permittedFileExtensions.includes(extension)
              )
                throw new Error('Invalid file extension.')

              onProgress?.({ percent: 50 })

              setUploading(true)
              const { data } = await rest.post(action, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
              })
              onSuccess?.(data)
            } catch (err: any) {
              errorHandler(err)
              onError?.(err)
            } finally {
              setUploading(false)
            }
          }}
          onChange={(change) => {
            const { file } = change
            const { status, name, error } = file
            if (status === 'done') {
              simpleSuccessNotification(`${name} file uploaded successfully.`)
              refetch()
              setUploading(false)
            } else if (status === 'error') {
              const workarounds = getWorkarounds(parent)
              errorHandler(error ?? `Failed to upload ${name}`, workarounds)
              refetch()
              setUploading(false)
            } else if (status === 'uploading') setUploading(true)
            onChange?.(change)
          }}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text" style={{ padding: 16 }}>
            Click or drag file to this area to upload
          </p>
        </Upload.Dragger>
      </Spin>
    </div>
  )
}

export default SharePointDragger
